import type { Building } from "building-types/building/buildingTypes";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { docToOptionDefinitions } from "doc/options/docGetInstanceOptionSets";
import { optionSetSelectedOptionId } from "doc/options/optionSetTools";
import { exteriorPaletteOptionSetId } from "doc/options/settingsOptionSet";
import { defaultExteriorPalette } from "doc/settings/defaultExteriorMaterials";
import { getExteriorPalette } from "../utils/builtinPaletteLoader";
import { memoAsync } from "../utils/memo";

function select({ doc, optionChoices, settings }: Building) {
  const exteriorMaterialSettings = settings.exteriorMaterials;
  const settingsId = exteriorMaterialSettings.id;

  return {
    exteriorMaterialSettings: settings.exteriorMaterials,
    palettes: doc.palettes,
    exteriorPaletteId: optionSetSelectedOptionId(
      docToOptionDefinitions(doc),
      optionChoices,
      exteriorPaletteOptionSetId(settingsId),
    ),
  };
}

async function generate(props: ReturnType<typeof select>) {
  const { exteriorMaterialSettings } = props;
  const issues: BuildingGenerationIssue[] = [];
  const exteriorPalette: ExteriorPalette = {
    ...((await exteriorPaletteFromSettings(props, issues)) ?? defaultExteriorPalette({ sortIndex: 0 })),
  };

  //load palette slot user defined names from settings
  if (exteriorPalette) {
    const id = exteriorPalette.id;
    const paletteSlotUserDefinedNames = exteriorMaterialSettings.paletteSlotsUserDefinedNames?.[id];
    if (paletteSlotUserDefinedNames) {
      for (const key in paletteSlotUserDefinedNames) {
        exteriorPalette[key] = { ...exteriorPalette[key], userDefinedName: paletteSlotUserDefinedNames[key] };
      }
    }
  }
  return { exteriorPalette, issues };
}

const exteriorPaletteFromSettings = async (
  { exteriorPaletteId, exteriorMaterialSettings, palettes }: ReturnType<typeof select>,
  issues: BuildingGenerationIssue[],
): Promise<ExteriorPalette> => {
  if (exteriorPaletteId === null) {
    exteriorPaletteId = exteriorMaterialSettings.palettes[0];
  }

  return (await getExteriorPalette(exteriorPaletteId, issues)) ?? (palettes[exteriorPaletteId] as ExteriorPalette);
};

export default memoAsync(select, generate);
